import { useMutation } from '@apollo/client';
import { ObservablePrimitive } from '@legendapp/state';
import { useObservable } from '@legendapp/state/react';
import * as icons from '@mui/icons-material';
import { Card, IconButton, Stack, Typography } from '@mui/material';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { MetadataItem } from 'components/common/metadata';
import { AudioPreviewButton, validateIvrUrls } from 'components/views/audio/player';
import { FormTextField } from 'components/views/forms/form-text-field';
import { useSnackbar } from 'providers/snackbar';
import { FC, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import {
  UpdateCampaignIvrUrls,
  UpdateCampaignIvrUrlsVariables,
} from 'types/apollo/UpdateCampaignIvrUrls';
import { UpdateCampaignIvrUrlsMutation } from './ivrs.graphql';

interface IvrUrlFormValues {
  inboundIvrAudioUrl: string;
  scheduledIvrAudioUrl: string;
  unscheduledIvrAudioUrl: string;
}

type IvrUrlFieldName = keyof IvrUrlFormValues;

const urlFields: { name: IvrUrlFieldName; label: string }[] = [
  { name: 'inboundIvrAudioUrl', label: 'Inbound' },
  { name: 'scheduledIvrAudioUrl', label: 'Scheduled' },
  { name: 'unscheduledIvrAudioUrl', label: 'Unscheduled' },
];

const IvrUrlViewRow = ({
  label,
  value,
  currentlyPlayingUrl,
  readonly,
}: {
  label: string;
  value: string | null;
  currentlyPlayingUrl: ObservablePrimitive<string>;
  readonly?: boolean;
}) => {
  if (readonly) {
    if (!value) return null;
    return (
      <Stack sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
        <AudioPreviewButton url={value} currentlyPlayingUrl={currentlyPlayingUrl} showSeekBar />
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center">
      {value && <AudioPreviewButton url={value} currentlyPlayingUrl={currentlyPlayingUrl} />}
      <MetadataItem label={label} value={value ?? 'Not set'} />
    </Stack>
  );
};

const IvrUrlEditRow = ({
  name,
  label,
  currentlyPlayingUrl,
}: {
  name: IvrUrlFieldName;
  label: string;
  currentlyPlayingUrl: ObservablePrimitive<string>;
}) => {
  const form = useFormContext<IvrUrlFormValues>();
  const [previewUrl, setPreviewUrl] = useState(form.getValues(name));

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onBlur={() => setPreviewUrl(form.getValues(name))}
    >
      <FormTextField form={form} name={name} label={label} optional />
      <AudioPreviewButton
        key={previewUrl}
        url={previewUrl}
        currentlyPlayingUrl={currentlyPlayingUrl}
        showSeekBar
      />
    </Stack>
  );
};

interface CampaignIvrUrlsCardProps {
  campaignId: string;
  companyId?: string;
  inboundIvrAudioUrl: string | null;
  scheduledIvrAudioUrl: string | null;
  unscheduledIvrAudioUrl: string | null;
  onSaved: () => void;
  readonly?: boolean;
}

export const CampaignIvrUrlsCard: FC<CampaignIvrUrlsCardProps> = ({
  campaignId,
  companyId,
  inboundIvrAudioUrl,
  scheduledIvrAudioUrl,
  unscheduledIvrAudioUrl,
  onSaved,
  readonly,
}) => {
  const currentlyPlayingUrl = useObservable<string>();
  const [editing, setEditing] = useState(false);
  const { openSnackbar } = useSnackbar();

  const form = useForm<IvrUrlFormValues>({
    defaultValues: {
      inboundIvrAudioUrl: inboundIvrAudioUrl ?? '',
      scheduledIvrAudioUrl: scheduledIvrAudioUrl ?? '',
      unscheduledIvrAudioUrl: unscheduledIvrAudioUrl ?? '',
    },
    mode: 'all',
  });

  const [updateUrls] = useMutation<UpdateCampaignIvrUrls, UpdateCampaignIvrUrlsVariables>(
    UpdateCampaignIvrUrlsMutation,
  );

  const handleSave = async (values: IvrUrlFormValues) => {
    if (!companyId) return;

    const validation = await validateIvrUrls(
      urlFields.map((f) => ({ label: f.label, url: values[f.name] })),
    );
    if (!validation.valid) {
      openSnackbar({ message: validation.message, severity: 'error' });
      return;
    }

    try {
      await updateUrls({
        variables: {
          campaignId,
          companyId,
          input: {
            inboundIvrAudioUrl: values.inboundIvrAudioUrl || null,
            scheduledIvrAudioUrl: values.scheduledIvrAudioUrl || null,
            unscheduledIvrAudioUrl: values.unscheduledIvrAudioUrl || null,
          },
        },
      });
      form.reset(values);
      setEditing(false);
      openSnackbar({ message: 'IVR Audio URLs updated', severity: 'success' });
      onSaved();
    } catch {
      openSnackbar({ message: 'Failed to update IVR Audio URLs', severity: 'error' });
    }
  };

  const handleCancel = () => {
    form.reset();
    setEditing(false);
  };

  const values = { inboundIvrAudioUrl, scheduledIvrAudioUrl, unscheduledIvrAudioUrl };

  return (
    <Card variant="outlined" sx={{ marginTop: 2, padding: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">IVR Audios</Typography>
        {!readonly && !editing && (
          <IconButton size="small" onClick={() => setEditing(true)}>
            <icons.Edit fontSize="small" />
          </IconButton>
        )}
      </Stack>
      {editing ? (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleSave)}>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {urlFields.map(({ name, label }) => (
                <IvrUrlEditRow
                  key={name}
                  name={name}
                  label={label}
                  currentlyPlayingUrl={currentlyPlayingUrl}
                />
              ))}
            </Stack>
            <FormSubmitRow
              submitText="Save"
              onCancel={handleCancel}
              cancelAlwaysEnabled
              enableIfNotDirty
              sx={{ mt: 1 }}
            />
          </form>
        </FormProvider>
      ) : (
        urlFields.map(({ name, label }) => (
          <IvrUrlViewRow
            key={name}
            label={label}
            value={values[name]}
            currentlyPlayingUrl={currentlyPlayingUrl}
            readonly={readonly}
          />
        ))
      )}
    </Card>
  );
};
